import {
  Color,
  LinearFilter,
  LinearMipmapLinearFilter,
  Material,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera,
  PlaneBufferGeometry,
  Scene,
  ShaderMaterial,
  sRGBEncoding,
} from "three";
import { KTX2Loader } from "./lib/KTX2Loader.js";
import ManifestoDistortShader from "../shaders/ManifestoDistortShader/ManifestoDistortShader";

import Easing from "easing-functions";
import { setTextureProperties } from "../util/three-utils";

const SHOW_HIDE_SPEED = 1.1;
const SHOW_HIDE_SHIM_SPEED = 1.3;

const SHIM_OPACITY = 0.4;

class ManifestoSlide extends Mesh {
  private ar: number;
  private time = 0;
  private visibilityProgress = 0;
  private doShow = false;
  private doHide = false;

  constructor(geom, material, texture, name) {
    super(geom, material);
    this.name = name;
    this.ar = texture.image.width / texture.image.height;
    (this.material as ShaderMaterial).uniforms.tDiffuse.value = texture;
    (this.material as ShaderMaterial).uniforms.uSeed.value = Math.random();
    this.material.uniformsNeedUpdate = true;
  }

  public onResize(width: number, height: number): void {
    this.scale.set(width, height, 1);
    const vpAR = width / height;

    if (width <= 768) {
      (this.material as ShaderMaterial).uniforms.uScale.value = 0.95;
    } else if (width > 1900) {
      (this.material as ShaderMaterial).uniforms.uScale.value = 0.4;
    } else {
      (this.material as ShaderMaterial).uniforms.uScale.value = 0.5;
    }

    (this.material as ShaderMaterial).uniforms.uAr.value = this.ar / vpAR;
  }

  public update(delta) {
    this.time += (1 / 60) * delta;
    (this.material as ShaderMaterial).uniforms.uTime.value = this.time;

    if (this.doShow) {
      this.visibilityProgress += (1 / (60 * SHOW_HIDE_SPEED)) * delta;

      if (this.visibilityProgress >= 1) {
        this.visibilityProgress = 1;
        this.doShow = false;
      }

      (this.material as ShaderMaterial).uniforms.uVisibilityProgress.value =
        Easing.Quadratic.Out(this.visibilityProgress);
    }

    if (this.doHide) {
      this.visibilityProgress -= (1 / (60 * SHOW_HIDE_SPEED)) * delta;

      if (this.visibilityProgress <= 0) {
        this.visibilityProgress = 0;
        this.doHide = false;
      }

      (this.material as ShaderMaterial).uniforms.uVisibilityProgress.value =
        Easing.Quadratic.In(this.visibilityProgress);
    }
  }

  public set isVisible(isVisible) {
    if (isVisible) {
      this.doShow = true;
      this.doHide = false;
    } else {
      this.doHide = true;
      this.doShow = false;
    }
  }
}

class ManifestoScene extends Scene {
  public camera = new OrthographicCamera(1, 1, 1, 1, 0.1, 9999);

  private currentSlideIndex: number | null = null;
  private slides: ManifestoSlide[] = [];
  private textureSrcs: string[];
  private textureLoader = new KTX2Loader();
  private shim: Mesh;

  private visibilityProgress = 0;
  private doShow = false;
  private doHide = false;

  constructor(textureSrcs: string[]) {
    super();

    this.textureLoader.setTranscoderPath("libs/basis/");
    this.textureLoader.detectSupport((window as any).webGlView.renderer);

    this.textureSrcs = textureSrcs;
    this.add(this.camera);
    this.camera.position.z = -1;
    this.camera.lookAt(this.position);

    const geom = new PlaneBufferGeometry(1, 1);
    const material = new MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0,
    });
    this.shim = new Mesh(geom, material);
    this.add(this.shim);
  }

  public async init(): Promise<void> {
    const textures = await Promise.all(
      this.textureSrcs.map((src) => {
        return this.textureLoader.loadAsync(src);
      })
    );
    this.textureLoader.dispose();
    textures.forEach((texture, index) => {
      texture.encoding = sRGBEncoding;
      texture.flipY = false;
      const geom = new PlaneBufferGeometry(1, 1).rotateY(Math.PI);
      const material = new ShaderMaterial(
        JSON.parse(JSON.stringify(ManifestoDistortShader))
      );
      const mesh = new ManifestoSlide(
        geom,
        material,
        texture,
        `slide-${index}`
      );
      this.slides.push(mesh);
      this.add(mesh);
    });

    setTextureProperties(this, {
      encoding: sRGBEncoding,
      magFilter: LinearFilter,
      minFilter: LinearMipmapLinearFilter,
      anisotropy: (
        window as any
      ).webGlView.renderer.capabilities.getMaxAnisotropy(),
    });
  }

  public set isVisible(isVisible) {
    if (isVisible) {
      this.doShow = true;
      this.doHide = false;
    } else {
      this.doHide = true;
      this.doShow = false;

      if (this.currentSlideIndex !== null) {
        this.slides[this.currentSlideIndex].isVisible = false;
      }
    }
  }

  public set slideIndex(index: number) {
    if (this.currentSlideIndex !== null) {
      this.slides[this.currentSlideIndex].isVisible = false;
    }

    this.currentSlideIndex = index;
    this.slides[this.currentSlideIndex].isVisible = true;
  }

  public onResize(width: number, height: number): void {
    this.camera.left = width / -2;
    this.camera.right = width / 2;
    this.camera.top = height / -2;
    this.camera.bottom = height / 2;
    this.camera.updateProjectionMatrix();
    this.shim.scale.set(width + 2, height + 2, 1);

    this.slides.forEach((mesh) => {
      mesh.onResize(width, height);
    });
  }

  public update(delta): void {
    this.slides.forEach((mesh) => {
      mesh.update(delta);
    });

    if (this.doShow) {
      this.visibilityProgress += (1 / (60 * SHOW_HIDE_SHIM_SPEED)) * delta;

      if (this.visibilityProgress >= 1) {
        this.visibilityProgress = 1;
        this.doShow = false;
      }

      (this.shim.material as Material).opacity =
        SHIM_OPACITY * Easing.Quadratic.Out(this.visibilityProgress);
    }

    if (this.doHide) {
      this.visibilityProgress -= (1 / (60 * SHOW_HIDE_SHIM_SPEED)) * delta;

      if (this.visibilityProgress <= 0) {
        this.visibilityProgress = 0;
        this.doHide = false;
      }

      (this.shim.material as Material).opacity =
        SHIM_OPACITY * Easing.Quadratic.In(this.visibilityProgress);
    }
  }
}

export default ManifestoScene;
